import type { SceneObject } from "../SceneHost";
import { sortSceneObjects } from "../render/sceneSort";
import { nextZIndex } from "../zIndex";
import { pointInObjectSpace, rotatedObjectBounds } from "../transform/rotate";

export function isPdfDocumentObject(
  obj: SceneObject | null | undefined
): obj is SceneObject & {
  type: "pdf_document";
  pageAssetIds: string[];
  pageCount: number;
  currentPage: number;
} {
  return (
    Boolean(obj) &&
    obj!.type === "pdf_document" &&
    Array.isArray(obj!.pageAssetIds) &&
    typeof obj!.pageCount === "number"
  );
}

export function isImageDocumentObject(
  obj: SceneObject | null | undefined
): obj is SceneObject & { type: "image" } {
  return Boolean(obj) && obj!.type === "image";
}

/** PDF или фото — хост для аннотаций (штрихи, текст и т.д.). */
export function isDocumentHostObject(obj: SceneObject | null | undefined): boolean {
  return isPdfDocumentObject(obj) || isImageDocumentObject(obj);
}

export function pdfCurrentPageAssetId(obj: SceneObject): string | null {
  if (!isPdfDocumentObject(obj)) return null;
  const page = typeof obj.currentPage === "number" ? obj.currentPage : 0;
  const id = obj.pageAssetIds[page];
  return typeof id === "string" ? id : null;
}

export function pdfPageAssetReady(obj: SceneObject, pageIndex: number): boolean {
  if (!isPdfDocumentObject(obj)) return false;
  return typeof obj.pageAssetIds[pageIndex] === "string";
}

export function isPdfBroadcastActive(obj: SceneObject): boolean {
  return isPdfDocumentObject(obj) && obj.displayMode === "presentation";
}

/** Листать / вести трансляцию может только ведущий (или редактор вне трансляции). */
export function canControlPdfDocument(
  obj: SceneObject,
  sessionPeerId: string | null,
  canEdit: boolean
): boolean {
  if (!isPdfDocumentObject(obj)) return false;
  if (isPdfBroadcastActive(obj)) {
    const leader = typeof obj.presentationLeaderId === "string" ? obj.presentationLeaderId : "";
    if (leader && sessionPeerId && leader === sessionPeerId) return true;
    // После reconnect peerId меняется — иначе «Вперёд» серое и «не щёлкается».
    // Редактор доски может продолжить вести/листать.
    return Boolean(canEdit && sessionPeerId);
  }
  if (!canEdit) return Boolean(obj.allowViewerPageNav);
  return true;
}

type DocumentContext = { documentId: string; documentPage: number };

function pdfPage(obj: SceneObject): number {
  return typeof obj.currentPage === "number" ? obj.currentPage : 0;
}

function hostBounds(obj: SceneObject): { x: number; y: number; w: number; h: number } | null {
  if (!isDocumentHostObject(obj)) return null;
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  if (w < 8 || h < 8) return null;
  return { x: obj.x, y: obj.y, w, h };
}

function rangesOverlap(a1: number, a2: number, b1: number, b2: number): boolean {
  return Math.max(a1, b1) < Math.min(a2, b2);
}

function pointInBounds(
  b: { x: number; y: number; w: number; h: number },
  x: number,
  y: number
): boolean {
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

/**
 * Лежит ли точка на самой картинке (или странице PDF) с учётом поворота.
 *
 * Повёрнутую картинку доска считала лежащей как прежде, и к ней цеплялось
 * всё, что попало в её СТАРЫЙ прямоугольник: подписи рядом становились
 * «надписями на картинке» и уезжали вместе с ней.
 */
function pointOnHost(obj: SceneObject, x: number, y: number): boolean {
  const b = hostBounds(obj);
  if (!b) return false;
  const [px, py] = pointInObjectSpace(obj, x, y);
  return pointInBounds(b, px, py);
}

function strokeBounds(points: [number, number][]): { x: number; y: number; w: number; h: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 1, h: 1 };
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) };
}

function documentContextForHost(obj: SceneObject): DocumentContext {
  return {
    documentId: obj.id,
    documentPage: isPdfDocumentObject(obj) ? pdfPage(obj) : 0,
  };
}

function hostsTopFirst(objects: SceneObject[]): SceneObject[] {
  return sortSceneObjects(objects as Record<string, unknown>[]) as SceneObject[];
}

export function documentContextAt(objects: SceneObject[], x: number, y: number): DocumentContext | null {
  const sorted = hostsTopFirst(objects);
  for (let i = sorted.length - 1; i >= 0; i--) {
    const obj = sorted[i]!;
    if (pointOnHost(obj, x, y)) {
      return documentContextForHost(obj);
    }
  }
  return null;
}

/**
 * Привязка штриха к PDF/фото: по пересечению bbox и по числу точек внутри.
 */
export function documentContextForStroke(
  objects: SceneObject[],
  points: [number, number][]
): DocumentContext | null {
  if (points.length < 2) {
    return documentContextAt(objects, points[0]?.[0] ?? 0, points[0]?.[1] ?? 0);
  }

  const stroke = strokeBounds(points);
  const sorted = hostsTopFirst(objects);
  let best: (DocumentContext & { score: number }) | null = null;

  for (let i = sorted.length - 1; i >= 0; i--) {
    const obj = sorted[i]!;
    const b = hostBounds(obj);
    if (!b) continue;

    // Грубая прикидка — по габариту повёрнутого объекта, точная — по точкам.
    const gab = rotatedObjectBounds(obj);
    const overlaps =
      rangesOverlap(stroke.x, stroke.x + stroke.w, gab.x, gab.x + gab.w) &&
      rangesOverlap(stroke.y, stroke.y + stroke.h, gab.y, gab.y + gab.h);
    if (!overlaps) continue;

    let inside = 0;
    for (const [px, py] of points) {
      if (pointOnHost(obj, px, py)) inside++;
    }
    const score = inside > 0 ? inside * 1000 + b.w * b.h : b.w * b.h * 0.01;
    const ctx = documentContextForHost(obj);
    if (!best || score > best.score) best = { ...ctx, score };
  }

  return best ? { documentId: best.documentId, documentPage: best.documentPage } : null;
}

export function applyDocumentContext(
  obj: SceneObject,
  ctx: { documentId: string; documentPage: number } | null
): void {
  if (!ctx) return;
  obj.documentId = ctx.documentId;
  obj.documentPage = ctx.documentPage;
}

/**
 * Поднять zIndex аннотации над PDF/фото, чтобы штрих не уходил под хост.
 *
 * Берём не «на единицу выше хоста», а самый верх доски — то же, что получает
 * любой новый объект. Иначе штрих по картинке, которая лежит в самом низу,
 * получал бы её же низкий номер и прятался под всем остальным: формально
 * выше картинки, а на деле не виден.
 */
export function ensureAnnotationAboveDocument(
  obj: SceneObject,
  objects: SceneObject[]
): void {
  if (!obj.documentId) return;
  const doc = objects.find((o) => o.id === obj.documentId);
  if (!doc) return;
  const docZ = typeof doc.zIndex === "string" ? Number(doc.zIndex) : 0;
  const objZ = typeof obj.zIndex === "string" ? Number(obj.zIndex) : 0;
  const base = Number.isFinite(docZ) ? docZ : 0;
  const target = Math.max(base + 1, Number(nextZIndex(objects as { zIndex?: string }[])));
  if (!Number.isFinite(objZ) || objZ < target) {
    obj.zIndex = String(target);
  }
}

export function buildSceneObjectMap(objects: SceneObject[]): Map<string, SceneObject> {
  const map = new Map<string, SceneObject>();
  for (let i = 0; i < objects.length; i++) {
    const o = objects[i]!;
    map.set(o.id, o);
  }
  return map;
}

/** Аннотации PDF — только на текущей странице; на фото — всегда; глобальные объекты — всегда. */
export function shouldRenderSceneObject(
  obj: SceneObject,
  objectsOrMap: SceneObject[] | Map<string, SceneObject>
): boolean {
  if (!obj.documentId) return true;
  const doc =
    objectsOrMap instanceof Map
      ? objectsOrMap.get(obj.documentId)
      : objectsOrMap.find((o) => o.id === obj.documentId);
  if (!doc) return true;
  if (isImageDocumentObject(doc)) return true;
  if (!isPdfDocumentObject(doc)) return true;
  const page = typeof obj.documentPage === "number" ? obj.documentPage : 0;
  const current = typeof doc.currentPage === "number" ? doc.currentPage : 0;
  return page === current;
}

export function collectPdfAnnotationIds(objects: SceneObject[], documentId: string): string[] {
  return collectDocumentAnnotationIds(objects, documentId);
}

export function collectDocumentAnnotationIds(objects: SceneObject[], documentId: string): string[] {
  return objects.filter((o) => o.documentId === documentId && o.id !== documentId).map((o) => o.id);
}

export function computePdfMovePatches(
  objects: SceneObject[],
  documentId: string,
  dx: number,
  dy: number,
  excludeIds?: Set<string>
): { id: string; patch: Record<string, unknown> }[] {
  return computeDocumentMovePatches(objects, documentId, dx, dy, excludeIds);
}

export function computeDocumentMovePatches(
  objects: SceneObject[],
  documentId: string,
  dx: number,
  dy: number,
  excludeIds?: Set<string>
): { id: string; patch: Record<string, unknown> }[] {
  if (!dx && !dy) return [];
  return objects
    .filter(
      (o) =>
        o.documentId === documentId && o.id !== documentId && !(excludeIds && excludeIds.has(o.id))
    )
    .map((o) => {
      const patch: Record<string, unknown> = {
        x: Math.round(o.x + dx),
        y: Math.round(o.y + dy),
      };
      if (Array.isArray(o.points) && o.points.length >= 2) {
        patch.points = o.points.map((p: [number, number]) => [
          Math.round(p[0] + dx),
          Math.round(p[1] + dy),
        ]);
      }
      return { id: o.id, patch };
    });
}
